import React from 'react';

interface PaymentEvent {
  paymentType: string;
  commissionCents: number;
  settlementDate: string;
  invoicedDate: string;
  brokerName: string;
  lender: string;
  ingestionTimestamp: string;
  runNumber: number;
  runDate: string;
  comments: string | null;
}

interface PaymentHistoryTimelineProps {
  events: PaymentEvent[];
}

export function PaymentHistoryTimeline({ events }: PaymentHistoryTimelineProps) {
  if (events.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        No payment history available for this loan.
      </div>
    );
  }

  // Sort events by invoiced date (most recent first), then by run number
  // invoicedDate is the actual payment date, more accurate than runDate
  const sortedEvents = [...events].sort((a, b) => {
    const dateA = new Date(a.invoicedDate || a.runDate).getTime();
    const dateB = new Date(b.invoicedDate || b.runDate).getTime();

    // Primary sort: invoiced date descending (most recent first)
    if (dateB !== dateA) {
      return dateB - dateA;
    }

    // Secondary sort: run number descending
    return (b.runNumber || 0) - (a.runNumber || 0);
  });

  return (
    <div className="flow-root">
      <ul className="-mb-8">
        {sortedEvents.map((event, eventIdx) => (
          <li key={eventIdx}>
            <div className="relative pb-8">
              {eventIdx !== sortedEvents.length - 1 && (
                <span
                  className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-600"
                  aria-hidden="true"
                />
              )}
              <div className="relative flex space-x-3">
                <div className="flex-shrink-0">
                  <span
                    className={`h-8 w-8 rounded-full flex items-center justify-center ring-4 ring-gray-800 ${getPaymentTypeColor(
                      event.paymentType
                    )}`}
                  >
                    {getPaymentTypeIcon(event.paymentType)}
                  </span>
                </div>
                <div className="flex min-w-0 flex-1 justify-between space-x-2 pt-1.5">
                  <div>
                    <p className="text-sm font-medium text-white">
                      {formatPaymentType(event.paymentType)}
                      <span className="ml-2 text-gray-300">
                        {formatMoney(event.commissionCents)}
                      </span>
                    </p>
                    <p className="mt-0.5 text-xs text-gray-400">
                      Run {event.runNumber} - {formatDate(event.invoicedDate || event.runDate)}
                    </p>
                    {event.comments && (
                      <p className="mt-1 text-xs text-gray-400 italic">{event.comments}</p>
                    )}
                  </div>
                  <div className="whitespace-nowrap text-right text-sm text-gray-400">
                    <time dateTime={event.invoicedDate || event.runDate}>
                      {formatRelativeDate(event.invoicedDate || event.runDate)}
                    </time>
                  </div>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function getPaymentTypeColor(paymentType: string): string {
  const lowerType = paymentType.toLowerCase();

  if (lowerType.includes('upfront')) {
    return 'bg-green-500';
  }
  if (lowerType.includes('trail') || lowerType.includes('arrears')) {
    return 'bg-blue-500';
  }
  if (lowerType.includes('discharged')) {
    return 'bg-red-500';
  }
  if (lowerType.includes('deduction') || lowerType.includes('clawback')) {
    return 'bg-amber-500';
  }

  return 'bg-gray-500';
}

function getPaymentTypeIcon(paymentType: string): React.ReactNode {
  const lowerType = paymentType.toLowerCase();

  if (lowerType.includes('upfront')) {
    return (
      <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
    );
  }
  if (lowerType.includes('trail') || lowerType.includes('arrears')) {
    return (
      <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
          clipRule="evenodd"
        />
      </svg>
    );
  }
  if (lowerType.includes('discharged')) {
    return (
      <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          clipRule="evenodd"
        />
      </svg>
    );
  }

  return (
    <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function formatPaymentType(paymentType: string): string {
  // Convert snake_case or kebab-case to Title Case
  return paymentType
    .split(/[_-]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function formatMoney(cents: number): string {
  const amount = cents / 100;
  const isNegative = amount < 0;
  const absAmount = Math.abs(amount);

  const formatted = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(absAmount);

  return isNegative ? `-${formatted}` : formatted;
}

function formatDate(dateString: string): string {
  if (!dateString || dateString === 'N/A') {
    return dateString || 'N/A';
  }

  try {
    const date = new Date(dateString);
    // Validate date before formatting
    if (isNaN(date.getTime())) {
      return dateString;
    }
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dateString;
  }
}

function formatRelativeDate(dateString: string): string {
  // Guard: Return fallback for invalid/missing dates
  if (!dateString || dateString === 'N/A') {
    return '';
  }

  try {
    const date = new Date(dateString);

    // Validate date before calculations to prevent NaN
    if (isNaN(date.getTime())) {
      return '';
    }

    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 0) return 'Future';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
    return `${Math.floor(diffInDays / 365)} years ago`;
  } catch {
    return '';
  }
}
